import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Stack,
  Title,
  Text,
  Paper,
  Timeline,
  Button,
  Box,
  Flex,
} from "@mantine/core";
import { useState, useEffect, useCallback, useRef } from "react";
import POASteps from "../components/PoaSteps";
import { z } from "zod";
import { FooterButtons } from "../components/footerButtons";
import { addSoftHyphens } from "../utils/addSoftHyphens";
import { isMobileDevice } from "../utils/useDeviceDetection";
import { ENUMPOASteps, usePOAStepsStore } from "../hooks/ui/poaStepsDataStore";
import BankIDLogo from "../assets/bank-id-logo.svg?react";
import {
  isFullCredentials,
  useUpdateCustomerData,
} from "../hooks/ui/customerDataStore";
import { POAStepNames } from "../types";
import { SigningComplete } from "../components/signingComplete";
import { detectBankIdRedirect } from "../utils/detectBankIdRedirect";
import timelineClasses from "../styles/timeline.module.css";

const initialHash = window.location.hash;

const signupParamsSchema = z.object({
  s: z.union([z.string(), z.null(), z.undefined()]),
  from: z.union([z.string(), z.null(), z.undefined()]),
  step: z.enum(POAStepNames).optional(),
});

export const Route = createFileRoute("/signup")({
  component: SignupPage,
  validateSearch: signupParamsSchema,
});

const isMobile = isMobileDevice();

function SignupPage() {
  const searchParams = Route.useSearch();
  const navigate = useNavigate();
  const {
    currentStep,
    setStep,
    canAccessStep,
    getHighestAccessibleStep,
    setApp,
  } = usePOAStepsStore();
  const { customerData } = useUpdateCustomerData();
  const hasValidCredentials = isFullCredentials(customerData.credentials);

  const hasIdentified = !!(
    customerData.personalNumber && customerData.transactionId
  );
  const isFromLogin =
    searchParams.from === "login" && (hasValidCredentials || hasIdentified);

  const initialStepFromUrl = searchParams.step
    ? POAStepNames.indexOf(searchParams.step)
    : -1;
  const [showWelcome, setShowWelcome] = useState(initialStepFromUrl < 0);

  // Detect if this tab was opened by BankID redirect after signing
  const [returnedFromBankId] = useState(() =>
    detectBankIdRedirect({
      hash: initialHash,
      nonceKeys: ["bankid_poa_sign_nonce", "bankid_login_nonce"],
      condition:
        !sessionStorage.getItem("bankid_login_tab") &&
        !sessionStorage.getItem("bankid_poa_sign_tab"),
    }),
  );

  // Keep a stable ref for the non-step search params (s, from) so updateUrlWithStep doesn't depend on searchParams and stays stable.
  const stableSearchParamsRef = useRef({
    s: searchParams.s,
    from: searchParams.from,
  });
  useEffect(() => {
    stableSearchParamsRef.current = {
      s: searchParams.s,
      from: searchParams.from,
    };
  }, [searchParams.s, searchParams.from]);

  // The step store is shared with /app-webview, which leaves `app` set.
  useEffect(() => setApp(false), [setApp]);

  useEffect(() => {
    if (searchParams.step) {
      const stepIndex = POAStepNames.indexOf(searchParams.step);
      if (stepIndex >= 0) {
        const targetStep = stepIndex as ENUMPOASteps;
        if (canAccessStep(targetStep)) {
          setStep(targetStep);
        } else {
          // Redirect to highest accessible step
          const highestStep = getHighestAccessibleStep();
          setStep(highestStep);
          navigate({
            to: "/signup",
            search: { ...searchParams, step: POAStepNames[highestStep] },
            replace: true,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateUrlWithStep = useCallback(
    (step: ENUMPOASteps) => {
      const stepName = POAStepNames[step];

      navigate({
        to: "/signup",
        search: { ...stableSearchParamsRef.current, step: stepName },
        replace: false,
      });
    },
    [navigate],
  );

  useEffect(() => {
    if (!showWelcome) {
      updateUrlWithStep(currentStep);
    }
  }, [currentStep, showWelcome, updateUrlWithStep]);

  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const stepParam = urlParams.get("step") as (typeof POAStepNames)[number];

      if (stepParam) {
        const stepIndex = POAStepNames.indexOf(stepParam);
        if (stepIndex >= 0 && stepIndex !== currentStep) {
          const targetStep = stepIndex as ENUMPOASteps;
          if (canAccessStep(targetStep)) {
            setStep(targetStep);
          } else {
            const highestStep = getHighestAccessibleStep();
            setStep(highestStep);
            const params = new URLSearchParams(window.location.search);
            params.set("step", POAStepNames[highestStep]);
            window.history.replaceState(
              null,
              "",
              `/signup?${params.toString()}`,
            );
          }
        }
      } else {
        setShowWelcome(true);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [currentStep, setStep, canAccessStep, getHighestAccessibleStep]);

  const getMeetingBlock = () => (
    <Timeline.Item bullet={isFromLogin ? 3 : 4}>
      {import.meta.env.VITE_BOOK_MEETING_ENABLED ? (
        <>
          <Title order={3} size="h5">
            Boka ett rådgivningsmöte
          </Title>
          <Text c="dimmed" size="sm">
            Du bokar in en tid som passar dig. Fram till mötet analyserar vi din
            pension och förbereder personliga råd om hur du kan förbättra ditt
            sparande.
          </Text>
        </>
      ) : (
        <>
          <Title order={3} size="h5">
            Invänta samtal för att boka ett rådgivningsmöte
          </Title>
          <Text c="dimmed" size="sm">
            När vi har fått in all information om dina pensionsavtal, ringer vi
            dig för att boka ett förutsättningslöst rådgivningsmöte med en av
            våra pensionsrådgivare.
          </Text>
        </>
      )}
    </Timeline.Item>
  );

  const handleContinue = () => {
    if (isFromLogin) {
      setStep(ENUMPOASteps.EMAILANDPHONE);
    } else {
      setStep(ENUMPOASteps.IDENTIFY);
    }
    setShowWelcome(false);
  };

  const handleCancel = () => {
    setShowWelcome(true);
    // Clear step from URL when going back to welcome
    const params = stableSearchParamsRef.current;
    const hasParams = params.s != null || params.from != null;
    navigate({
      to: "/signup",
      search: hasParams ? params : undefined,
      replace: true,
    });
  };

  if (returnedFromBankId) {
    return (
      <SigningComplete
        description="Du kan stänga den här fliken och gå tillbaka till den ursprungliga fliken för att fortsätta."
        showCloseButton
      />
    );
  }

  return (
    <Paper
      my={isMobile ? "1rem" : "4rem"}
      mx={isMobile ? 0 : "3rem"}
      p={isMobile ? "1rem" : "2rem"}
      radius={"24px"}
    >
      {showWelcome ? (
        <Stack>
          <Title ta="center" ff={"var(--wallet-font-family-sambla-sans)"}>
            {isFromLogin ? "Du är inte kund hos oss än!" : "Välkommen!"}
          </Title>
          <Text px={isMobile ? 5 : "3rem"}>
            {isFromLogin
              ? "För att kunna logga in behöver du vara kund hos oss. Innan du blir kund hos oss måste vi hämta en informationsfullmakt för att kunna se över ditt pensionssparande. Det tar bara någon minut."
              : "Du är på god väg att förbättra din pension. Innan vi kan rådge dig måste vi få lite information om dig och ditt sparande."}
          </Text>
          <Paper
            py={isMobile ? 15 : 30}
            px={isMobile ? 5 : "3rem"}
            radius={"24px"}
            mb={"2rem"}
          >
            <Title
              order={2}
              size="h3"
              mb={"xl"}
              mx={3}
              ff={"var(--wallet-font-family-sambla-sans)"}
            >
              Så här kommer det att gå till
            </Title>
            <Timeline
              className={timelineClasses.timeline}
              active={isFromLogin ? 3 : 4}
              bulletSize={28}
              color={"var(--wallet-color-masterbrand-brand-green)"}
            >
              {!isFromLogin && (
                <Timeline.Item bullet={1}>
                  <Title order={3} size="h5">
                    Identifiera dig med BankID
                  </Title>
                  <Text c="dimmed" size="sm">
                    Genom att verifiera din identitet kan vi se till att din
                    information är korrekt och att den bara delas med rätt
                    person. På så sätt skyddar vi både dig och din personliga
                    data.
                  </Text>
                </Timeline.Item>
              )}
              <Timeline.Item bullet={isFromLogin ? 1 : 2}>
                <Title order={3} size="h5">
                  Fyll i dina kontaktuppgifter
                </Title>
                <Text c="dimmed" size="sm">
                  Kontaktuppgifterna behövs för att kunna hämta en fullmakt och
                  för att kunna nå dig när det är dags för rådgivning.
                </Text>
              </Timeline.Item>
              <Timeline.Item bullet={isFromLogin ? 2 : 3}>
                <Title order={3} size="h5">
                  {addSoftHyphens("Skriv under en informationsfullmakt")}
                </Title>
                <Text c="dimmed" size="sm">
                  Fullmakten ger oss tillgång till information om din pension
                  och lägger grunden till din rådgivning.
                </Text>
              </Timeline.Item>
              {getMeetingBlock()}
            </Timeline>
          </Paper>
          <FooterButtons>
            <Button variant="default" onClick={handleCancel}>
              Avbryt
            </Button>
            <Button onClick={handleContinue}>
              <Flex component="span" align="center">
                <Box component="span" mr="0.5rem">
                  {isFromLogin ? "Gå vidare" : "Identifiera"}
                </Box>
                {!isFromLogin && <BankIDLogo role="img" aria-label="BankID" />}
              </Flex>
            </Button>
          </FooterButtons>
        </Stack>
      ) : (
        <POASteps setUserCancelled={handleCancel} />
      )}
    </Paper>
  );
}
